// New version of DocumentEdit.jsx using Tiptap instead of ReactQuill

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../services/api/axios';
import { parse } from 'node-html-parser';
import { DOCUMENT_VARIABLES, replaceVariablesWithDummyData } from './documentVariables';
import { PrimaryButton } from '../../components/Buttons';

import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Underline from '@tiptap/extension-underline'
import { Bold, Heading1, Heading2, Italic, List, ListOrdered, TableIcon, UnderlineIcon, Trash2, ArrowDownFromLine, ArrowUpFromLine, ArrowLeftFromLine, ArrowRightFromLine, Delete } from 'lucide-react';

// Toolbar component for Tiptap with improved UI and tooltips
const Toolbar = ({ editor, className = "" }) => {
  if (!editor) return null;

  const ToolbarButton = ({ onClick, isActive, disabled, title, children, className = "" }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        relative p-2.5 rounded-md transition-all duration-200 ease-in-out
        ${isActive 
          ? 'bg-blue-100 text-blue-700 border border-blue-200 shadow-sm' 
          : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900'
        }
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      title={title}
    >
      {children}
    </button>
  );

  const ToolbarSeparator = () => (
    <div className="w-px h-8 bg-gray-300 mx-1"></div>
  );

  return (
    <div className={`border-b border-gray-200 bg-white p-3 flex flex-wrap items-center gap-1 shadow-sm ${className}`}>
      {/* Text Formatting */}
      <div className="flex items-center gap-1">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="w-4 h-4"/>
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      {/* Headings */}
      <div className="flex items-center gap-1">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4"/>
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      {/* Lists */}
      <div className="flex items-center gap-1">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4"/>
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      {/* Table Controls */}
      <div className="flex items-center gap-1">
        <ToolbarButton
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title="Insert Table (3x3)"
        >
          <TableIcon className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().addColumnBefore().run()}
          disabled={!editor.can().addColumnBefore()}
          title="Add Column Before"
        >
          <ArrowLeftFromLine className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          disabled={!editor.can().addColumnAfter()}
          title="Add Column After"
        >
          <ArrowRightFromLine className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().deleteColumn().run()}
          disabled={!editor.can().deleteColumn()}
          title="Delete Column"
        >
          <Delete className="w-4 h-4"/>
        </ToolbarButton>
      </div>

      <ToolbarSeparator />

      {/* Row Controls */}
      <div className="flex items-center gap-1">
        <ToolbarButton
          onClick={() => editor.chain().focus().addRowBefore().run()}
          disabled={!editor.can().addRowBefore()}
          title="Add Row Before"
        >
          <ArrowUpFromLine className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().addRowAfter().run()}
          disabled={!editor.can().addRowAfter()}
          title="Add Row After"
        >
          <ArrowDownFromLine className="w-4 h-4"/>
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().deleteRow().run()}
          disabled={!editor.can().deleteRow()}
          title="Delete Row"
        >
          <Trash2 className="w-4 h-4"/>
        </ToolbarButton>
      </div>
    </div>
  );
};

const DocumentEdit = ({ documentType, documentData, onClose, onSaveSuccess }) => {
  const A4_PAGE_WIDTH_PX = 794;
  const A4_PAGE_HEIGHT_PX = 1123;

  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState('');
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [htmlPreviewUrl, setHtmlPreviewUrl] = useState(null);
  const [htmlPreviewScale, setHtmlPreviewScale] = useState(1);
  const [htmlPreviewZoom, setHtmlPreviewZoom] = useState(1);
  const htmlPreviewContainerRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable automatic formatting to preserve original styles
        paragraph: {
          HTMLAttributes: {},
        },
        heading: {
          HTMLAttributes: {},
        },
        listItem: {
          HTMLAttributes: {},
        },
        bold: {
          HTMLAttributes: {},
        },
        italic: {
          HTMLAttributes: {},
        },
        // Disable automatic list formatting
        bulletList: {
          HTMLAttributes: {},
        },
        orderedList: {
          HTMLAttributes: {},
        },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Underline,
    ],
    content: documentData?.content || '<p>Hello World!</p>',
    onUpdate({ editor }) {
      setContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none',
      },
    },
    // Preserve existing HTML structure and styles
    parseOptions: {
      preserveWhitespace: 'full',
    },
  });

  useEffect(() => {
    if (documentData?.content) setContent(documentData.content);
    if (documentData?.originalContent) setOriginalContent(documentData.originalContent);
  }, [documentData]);

  useEffect(() => {
    if (editor && documentData?.content) {
      editor.commands.setContent(documentData.content);
    }
  }, [editor, documentData?.content]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (htmlPreviewUrl) {
        window.URL.revokeObjectURL(htmlPreviewUrl);
      }
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [htmlPreviewUrl, pdfUrl]);

  // Keep HTML preview locked to A4 proportions and fit it inside the panel.
  useEffect(() => {
    if (!showHtmlPreview) return;

    const container = htmlPreviewContainerRef.current;
    if (!container) return;

    const updateScale = () => {
      const horizontalPadding = 32;
      const availableWidth = container.clientWidth - horizontalPadding;
      if (availableWidth <= 0) return;
      setHtmlPreviewScale(Math.min(1, availableWidth / A4_PAGE_WIDTH_PX));
    };

    updateScale();
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(container);
    window.addEventListener('resize', updateScale);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [showHtmlPreview]);

  const getUpdatedHtml = (useDummyDataForPreview = false) => {
    try {
      // Parse the original HTML from backend
      const root = parse(originalContent);
      const container = root.querySelector('.container');
      
      if (container) {
        let contentToUse = content;
        if (useDummyDataForPreview) {
          contentToUse = replaceVariablesWithDummyData(content);
        }
        
        // Clean up editor artifacts
        const cleanedContent = cleanHtmlContent(contentToUse);
        
        // Preserve original structure/styles and only merge edited content into it.
        const styledContent = mergeEditedContentIntoOriginalStructure(cleanedContent, originalContent);
        
        // Replace ONLY the content inside the container, preserving everything else
        container.innerHTML = styledContent;
        
        // Return the exact same structure as backend, just with updated content and restored styles
        return root.toString();
      }
    } catch (parseError) {
      console.error('Error updating HTML:', parseError);
    }
    
    // If parsing fails, return original content unchanged
    return originalContent;
  };

  // Preserve original layout/styles and update only editable content blocks.
  // This avoids formatting drift (e.g. unexpected center alignment) after edits.
  const mergeEditedContentIntoOriginalStructure = (editedContent, originalContent) => {
    try {
      const originalRoot = parse(originalContent);
      const originalContainer = originalRoot.querySelector('.container');
      
      if (!originalContainer) return editedContent;
      
      const editedRoot = parse(editedContent);

      const editableSelectors = 'p, li, td, th, h1, h2, h3, h4, h5, h6';
      const originalNodes = originalContainer.querySelectorAll(editableSelectors);
      const editedNodes = editedRoot.querySelectorAll(editableSelectors);

      // If no granular nodes are available, fallback to full content replacement.
      if (!originalNodes.length || !editedNodes.length) {
        return editedContent;
      }

      // Merge by tag sequence instead of raw index to avoid accidental duplication/misalignment.
      let originalCursor = 0;
      for (let i = 0; i < editedNodes.length && originalCursor < originalNodes.length; i += 1) {
        const editedTag = editedNodes[i].tagName;

        while (
          originalCursor < originalNodes.length &&
          originalNodes[originalCursor].tagName !== editedTag
        ) {
          originalCursor += 1;
        }

        if (originalCursor >= originalNodes.length) {
          break;
        }

        // Copy inner content only; keep original node attributes/styles/classes.
        originalNodes[originalCursor].innerHTML = editedNodes[i].innerHTML;
        originalCursor += 1;
      }

      return originalContainer.innerHTML;
    } catch (error) {
      console.error('Error preserving original styles/structure:', error);
      return editedContent;
    }
  };

  // Enhanced cleanup function - removes editor artifacts while preserving HTML structure
  const cleanHtmlContent = (htmlContent) => {
    try {
      // First, remove nested HTML document structure using regex (safer approach)
      let cleanedHtml = htmlContent
        .replace(/<!DOCTYPE[^>]*>/gi, '')
        .replace(/<html[^>]*>/gi, '')
        .replace(/<\/html>/gi, '')
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
        .replace(/<body[^>]*>/gi, '')
        .replace(/<\/body>/gi, '');
      
      // Parse the cleaned content
      const contentRoot = parse(cleanedHtml);
      
      // Remove any nested ql-editor divs and extract their content
      const qlEditors = contentRoot.querySelectorAll('.ql-editor');
      qlEditors.forEach((editor) => {
        // Replace the ql-editor div with its inner content
        editor.replaceWith(editor.innerHTML);
      });
      
      // Remove any malformed body tags inside paragraphs
      const paragraphs = contentRoot.querySelectorAll('p');
      paragraphs.forEach(p => {
        const bodyTags = p.querySelectorAll('body');
        bodyTags.forEach(body => {
          // Replace body tag with its inner content
          body.replaceWith(body.innerHTML);
        });
      });
      
      // Get the final cleaned HTML
      let result = contentRoot.toString();
      
      // Remove editor-paragraph class and preserve original inline styles
      result = result.replace(/class="editor-paragraph"/gi, '');
      
      // Clean up extra whitespace between tags (but preserve intentional spacing)
      result = result.replace(/>\s+</g, '><');
      
      // Remove empty paragraphs with just whitespace
      result = result.replace(/<p[^>]*>\s*<\/p>/gi, '');
      
      // NOTE: Do not remove generic empty divs.
      // Some templates use empty divs as structural spacers/anchors (e.g. footer placement),
      // and removing them can shift absolutely-positioned elements in HTML preview.
      
      // Clean up any remaining nested HTML structure
      result = result.replace(/<html[^>]*>/gi, '');
      result = result.replace(/<\/html>/gi, '');
      result = result.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
      result = result.replace(/<body[^>]*>/gi, '');
      result = result.replace(/<\/body>/gi, '');
      
      return result.trim();
    } catch (error) {
      console.error('Error cleaning HTML content:', error);
      // Fallback: basic string replacement
      return htmlContent
        .replace(/<!DOCTYPE[^>]*>/gi, '')
        .replace(/<html[^>]*>/gi, '')
        .replace(/<\/html>/gi, '')
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
        .replace(/<body[^>]*>/gi, '')
        .replace(/<\/body>/gi, '')
        .replace(/<p><body[^>]*>/gi, '')
        .replace(/<\/body><\/p>/gi, '')
        .replace(/<div class="ql-editor">/gi, '')
        .replace(/<\/div class="ql-editor">/gi, '')
        .replace(/class="editor-paragraph"/gi, '')
        .trim();
    }
  };

  const handleGeneratePdf = async () => {
    try {
      setIsGeneratingPdf(true);
      const completeHtml = getUpdatedHtml(true);
     
      const response = await axiosInstance.post(
        '/generate-test-pdf-from-html',
        completeHtml,
        {
          responseType: 'blob',
          headers: { 'Content-Type': 'text/html' },
        }
      );
      
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      
      // Optional: Show success message
      toast.success('PDF generated successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF preview');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const completeHtml = getUpdatedHtml(false);
      console.log("------------",documentData);
      await axiosInstance.put(
        '/customers/document',
        completeHtml,
        {
          params: {
            documentId: documentData?.documentId,
          },
        }
      );
      toast.success('Document saved successfully');
      if (onSaveSuccess) {
        onSaveSuccess({
          documentId: documentData?.documentId,
          documentType: documentData?.documentType || documentType,
          documentHtmlBytes: completeHtml,
        });
      }
      onClose();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Failed to save document');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInsertVariable = () => {
    if (!selectedVariable || !editor) return;
    editor.chain().focus().insertContent(selectedVariable).run();
  };

  const generateHtmlPreview = () => {
    const completeHtml = getUpdatedHtml(true);
    const blob = new Blob([completeHtml], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    setHtmlPreviewUrl(url);
  };

  const handleZoomIn = () => {
    setHtmlPreviewZoom((prev) => Math.min(2.5, +(prev + 0.1).toFixed(2)));
  };

  const handleZoomOut = () => {
    setHtmlPreviewZoom((prev) => Math.max(0.5, +(prev - 0.1).toFixed(2)));
  };

  const toggleHtmlPreview = () => {
    if (!showHtmlPreview) {
      generateHtmlPreview();
    } else {
      if (htmlPreviewUrl) {
        window.URL.revokeObjectURL(htmlPreviewUrl);
        setHtmlPreviewUrl(null);
      }
    }
    setShowHtmlPreview(!showHtmlPreview);
  };

  const variableList = DOCUMENT_VARIABLES[documentType] || [];

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
        <div className="flex flex-col h-full min-h-0">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Document Editor</h3>
            <div className="flex gap-2">
              <PrimaryButton 
                onClick={toggleHtmlPreview} 
                className={showHtmlPreview ? 'bg-blue-600' : 'bg-gray-600'}
              >
                {showHtmlPreview ? 'Hide HTML Preview' : 'Show HTML Preview'}
              </PrimaryButton>
              <PrimaryButton onClick={handleGeneratePdf} disabled={isGeneratingPdf}>
                {isGeneratingPdf ? 'Generating PDF...' : 'Preview PDF'}
              </PrimaryButton>
            </div>
          </div>

          {variableList.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">Only the following variables are available for this template.</div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Insert variable:</span>
                <select
                  className="border rounded px-2 py-1"
                  value={selectedVariable}
                  onChange={e => setSelectedVariable(e.target.value)}
                >
                  <option value="">Select variable</option>
                  {variableList.map(v => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
                <PrimaryButton onClick={handleInsertVariable} disabled={!selectedVariable}>
                  Insert
                </PrimaryButton>
              </div>
            </div>
          )}

          <Toolbar editor={editor} className="z-[9999] bg-white border rounded-t-lg border-b-0"/>
          <div className="flex-1 bg-white border rounded-b-lg overflow-hidden flex flex-col min-h-0">
            {showHtmlPreview ? (
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Processed HTML Preview (Same as PDF)</h4>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleZoomOut}
                        className="h-7 w-7 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                        title="Zoom Out"
                      >
                        -
                      </button>
                      <span className="text-xs text-gray-600 min-w-[52px] text-center">
                        {Math.round(htmlPreviewZoom * 100)}%
                      </span>
                      <button
                        type="button"
                        onClick={handleZoomIn}
                        className="h-7 w-7 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                        title="Zoom In"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded">
                    This shows the exact HTML that will be sent to the PDF generator with dummy data
                  </div>
                </div>
                <div ref={htmlPreviewContainerRef} className="flex-1 overflow-auto bg-gray-100 p-4">
                  {htmlPreviewUrl ? (
                    <div
                      className="mx-auto bg-white shadow-md"
                      style={{
                        width: `${A4_PAGE_WIDTH_PX}px`,
                        height: `${A4_PAGE_HEIGHT_PX}px`,
                        transform: `scale(${htmlPreviewScale * htmlPreviewZoom})`,
                        transformOrigin: 'top center',
                      }}
                    >
                      <iframe
                        src={htmlPreviewUrl}
                        title="HTML Preview"
                        sandbox="allow-scripts"
                        style={{
                          width: `${A4_PAGE_WIDTH_PX}px`,
                          height: `${A4_PAGE_HEIGHT_PX}px`,
                          border: 0,
                          display: 'block',
                        }}
                      />
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400">
                      Generating HTML preview...
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <EditorContent editor={editor} className="p-4 prose max-w-none" />
              </div>
            )}
            <style>{`
              .ProseMirror {
                outline: none;
                min-height: 400px;
                height: auto;
                white-space: pre-wrap;
                word-wrap: break-word;
              }
              .ProseMirror p {
                margin: 0.5em 0;
                min-height: 1.2em;
                line-height: 1.5;
              }
              .ProseMirror p:empty {
                min-height: 1.2em;
                display: block;
              }
              .ProseMirror h1 {
                font-size: 1.5em;
                font-weight: bold;
                margin: 1em 0 0.5em 0;
              }
              .ProseMirror h2 {
                font-size: 1.25em;
                font-weight: bold;
                margin: 1em 0 0.5em 0;
              }
              .ProseMirror ul, .ProseMirror ol {
                padding-left: 1.5em;
                margin: 0.5em 0;
              }
              .ProseMirror table {
                border-collapse: collapse;
                margin: 0.5em 0;
                width: 100%;
                table-layout: fixed;
              }
              .ProseMirror table td, .ProseMirror table th {
                border: 1px solid #ddd;
                padding: 0.5em;
                text-align: left;
                vertical-align: top;
                word-wrap: break-word;
              }
              .ProseMirror table th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              .ProseMirror table colgroup, .ProseMirror table col {
                display: none;
              }
              .editor-paragraph {
                margin: 0.5em 0;
                min-height: 1.2em;
                line-height: 1.5;
              }
              .editor-paragraph:empty {
                min-height: 1.2em;
                display: block;
              }
            `}</style>
          </div>
        </div>

        <div className="flex flex-col h-full min-h-0">
          <div className="flex-1 bg-gray-50 rounded-lg p-4 overflow-hidden">
            <h3 className="text-lg font-semibold mb-4">PDF Preview</h3>
            <div className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded mb-4">
              Sample data is displayed for preview purposes
            </div>
            <div className="relative h-full overflow-hidden">
              {pdfUrl ? (
                <iframe id="pdf-preview-iframe" src={pdfUrl} className="w-full h-full" title="PDF Preview" />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  {isGeneratingPdf ? 'Generating PDF...' : 'Click "Preview PDF" to generate'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-white border-t pt-4 pb-2 px-4 mt-4">
        <div className="flex justify-end gap-2">
          <PrimaryButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default DocumentEdit;
